import { useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal";
import ImageChatUploadInput from "../../../../parts/inputs/image-chat-upload";
import {
  parseAthletesOrganizationInfoToClientList,
  parseCoachInformationToChat,
} from "../../../../utils/user-helpers";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";

const MAX_PREVIEW_CHIPS = 8;

const formatChipName = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

export const NewGroupModalComponent = ({
  athletesToShow,
  usersOrganization,
  activeUser,
  referAsClients,
  allGroupChats,
  hasAutoManagedChat,
  isTeamCoach,
}) => {
  const { t } = useTranslation("coaching-portal");
  const { webAppTheme = "dark" } = activeUser;
  const activeUserId = activeUser._id;
  const { organizationActiveUsers = [] } = usersOrganization || {};
  const referName = referAsClients
    ? t("athletes.roleClient")
    : t("athletes.roleAthlete");
  const isOrganizationUser = !!activeUser.organizationId;
  const showCommunityTip =
    isOrganizationUser && !!activeUser.pushAccountInfo?.inAppCommunityEnabled;

  const allUsers = usersOrganization
    ? [
        ...organizationActiveUsers,
        ...parseAthletesOrganizationInfoToClientList(athletesToShow),
      ]
    : [
        ...parseCoachInformationToChat(activeUser),
        ...parseAthletesOrganizationInfoToClientList(athletesToShow),
      ];

  const activeAthletes = useMemo(
    () =>
      allUsers.filter(
        (u) => !!u.coachId && String(u.coachId) === String(activeUserId)
      ),
    [allUsers, activeUserId]
  );

  const [participantState, setParticipantState] = useState([activeUserId]);
  const [stepState, setStepState] = useState(1);
  const [isAutoManaged, setIsAutoManaged] = useState(false);
  const createGroupButtonRef = useRef(null);

  const handleSelectionChange = (value) => {
    if (String(value) !== String(activeUserId)) {
      let data = JSON.parse(JSON.stringify(participantState));
      if (!participantState.includes(value)) {
        if (participantState.length < 100) {
          data.length === 1 && data[0].length === 0
            ? (data = [value])
            : data.push(value);
        }
      } else {
        if (participantState.length > 0) {
          const filtered = data.filter((val) => val != value);
          data = filtered.length > 0 ? filtered : [activeUserId];
        }
      }
      setParticipantState(data);
    }
  };

  const handleToggleAutoManaged = () => {
    if (hasAutoManagedChat) return;
    const next = !isAutoManaged;
    setIsAutoManaged(next);
    if (next) {
      setParticipantState([activeUserId, ...activeAthletes.map((a) => a._id)]);
    } else {
      setParticipantState([activeUserId]);
    }
  };

  const handleNextStep = () => {
    if (stepState === 1) {
      if (isAutoManaged && hasAutoManagedChat) return;
      if (!isAutoManaged && participantState[0].length === 0) return;
      setStepState(2);
    } else {
      if (createGroupButtonRef.current) {
        createGroupButtonRef.current.click();
      }
    }
  };

  return (
    <Modal
      className={"create-chat-group-modal"}
      overflowModal={true}
      chatGroupEdit={true}
    >
      <div className="head border-divider">
        <h2 className="alt">{t("chat.newGroupModal.title")}</h2>
        <a
          href={`/actions/refresh?groupParticipants=&groupSubject=&allGroupChats=&groupChatId=&groupImage=&groupEdited=&modal=`}
        >
          <img
            className="icon-red"
            src="/img/new-icons/red-cross.svg"
            alt={t("chat.newGroupModal.dismissAlt")}
          />
        </a>
      </div>

      {stepState === 1 ? (
        <Step1NewGroup
          allUsers={allUsers}
          activeAthletes={activeAthletes}
          participantState={participantState}
          handleSelectionChange={handleSelectionChange}
          referAsClients={referAsClients}
          referName={referName}
          webAppTheme={webAppTheme}
          isAutoManaged={isAutoManaged}
          hasAutoManagedChat={hasAutoManagedChat}
          handleToggleAutoManaged={handleToggleAutoManaged}
          showCommunityTip={showCommunityTip}
          isTeamCoach={isTeamCoach}
        />
      ) : (
        <Step2NewGroup
          participantState={participantState}
          allUsers={allUsers}
          createGroupButtonRef={createGroupButtonRef}
          referName={referName}
          allGroupChats={allGroupChats}
          webAppTheme={webAppTheme}
          isAutoManaged={isAutoManaged}
        />
      )}
      <div className="modal-footer foot modal-footer-group-chat">
        <div className="footer-group-chat-modal">
          {stepState === 1 ? (
            <div>
              {!isAutoManaged && (
                <button
                  className="clear-all-group-chat"
                  onClick={() => {
                    if (!isAutoManaged) {
                      setParticipantState([activeUserId]);
                    }
                  }}
                >
                  {t("chat.newGroupModal.clearAll")}
                </button>
              )}
            </div>
          ) : (
            <div>
              <button
                className="back-group-chat"
                onClick={() => {
                  setStepState(1);
                }}
              >
                {t("chat.newGroupModal.back")}
              </button>
            </div>
          )}
          <div>
            <button
              id="next-group-chat"
              data-button-type="primary"
              className="next-group-chat"
              onClick={handleNextStep}
              disabled={isAutoManaged && hasAutoManagedChat}
              type={stepState === 1 ? "button" : "submit"}
            >
              <span>
                {stepState === 1
                  ? t("chat.newGroupModal.next")
                  : t("chat.newGroupModal.create")}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const AutoManagedToggle = ({
  isAutoManaged,
  hasAutoManagedChat,
  handleToggleAutoManaged,
}) => {
  const { t } = useTranslation("coaching-portal");
  return (
  <div className="auto-managed-toggle-group-chat">
    <div className="auto-managed-toggle-label">
      <SvgIcon
        src={`/img/new-icons/${isAutoManaged ? "automatic-green" : "automatic"}.svg`}
        noFilterOnLight={true}
        alt=""
      />
      <span>{t("chat.newGroupModal.autoManagedToggleLabel")}</span>
    </div>
    {hasAutoManagedChat ? (
      <ToolTip
        id="auto-managed-toggle-tooltip"
        body={t("chat.newGroupModal.autoManagedToggleTooltip")}
      >
        <input
          type="checkbox"
          role="switch"
          checked={isAutoManaged}
          disabled={true}
          onChange={handleToggleAutoManaged}
        />
      </ToolTip>
    ) : (
      <input
        type="checkbox"
        role="switch"
        checked={isAutoManaged}
        onChange={handleToggleAutoManaged}
      />
    )}
  </div>
  );
};

const AutoManagedInfoBox = ({ isAutoManaged, roleLower, rolesLower }) => {
  const { t } = useTranslation("coaching-portal");
  return (
  <div className={`chip ${isAutoManaged ? "success" : "info"} mb-4`}>
    <p>
      {isAutoManaged ? (
        <>
          <strong>{t("chat.newGroupModal.autoManagedInfoOnTitle")}</strong>{" "}
          {t("chat.newGroupModal.autoManagedInfoOnBody", {
            roleLower,
            rolesLower,
          })}
        </>
      ) : (
        t("chat.newGroupModal.autoManagedInfoOff", { rolesLower })
      )}
    </p>
  </div>
  );
};

const AutoManagedPreview = ({
  activeAthletes,
  webAppTheme,
  roleSingularLower,
  rolesLower,
}) => {
  const { t } = useTranslation("coaching-portal");
  const visibleAthletes = activeAthletes.slice(0, MAX_PREVIEW_CHIPS);
  const remaining = activeAthletes.length - visibleAthletes.length;

  return (
    <div className="auto-managed-preview-group-chat">
      <div className="auto-managed-preview-header">
        <SvgIcon src="/img/new-icons/people-circle-outline.svg" alt="" />
        <span>
          {t("chat.newGroupModal.autoManagedPreviewHeader", {
            count: activeAthletes.length,
            roleSingularLower,
            rolesLower,
          })}
        </span>
      </div>
      <div className="auto-managed-chips-container">
        {visibleAthletes.map((athlete) => (
          <div key={athlete._id} className="auto-managed-chip">
            <div
              className="auto-managed-chip-avatar"
              style={{
                background: `url(${getAvatarUrl(athlete, false, webAppTheme)})`,
                backgroundPosition: "center center",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
              }}
            ></div>
            <span>{formatChipName(athlete.name)}</span>
          </div>
        ))}
      </div>
      {remaining > 0 && (
        <span className="auto-managed-chips-more">
          {t("chat.newGroupModal.autoManagedChipsMore", { count: remaining })}
        </span>
      )}
    </div>
  );
};

const CommunityTip = () => {
  const { t } = useTranslation("coaching-portal");
  return (
  <div className="auto-managed-tip-group-chat">
    <span className="tip-icon">💡</span>
    <div>
      <p>
        {t("chat.newGroupModal.communityTipIntro")}
        <strong>{t("chat.newGroupModal.communityTipCommunityChat")}</strong>
        {t("chat.newGroupModal.communityTipOutro")}
      </p>
      <a href="/coaching-portal/community-chat">
        {t("chat.newGroupModal.communityTipLink")}
      </a>
    </div>
  </div>
  );
};

const Step1NewGroup = ({
  allUsers,
  activeAthletes,
  participantState,
  handleSelectionChange,
  referAsClients,
  referName,
  webAppTheme,
  isAutoManaged,
  hasAutoManagedChat,
  handleToggleAutoManaged,
  showCommunityTip,
  isTeamCoach,
}) => {
  const { t } = useTranslation("coaching-portal");
  const roleLower = referAsClients
    ? t("athletes.roleClientLower")
    : t("athletes.roleAthleteLower");
  const rolesLower = referAsClients
    ? t("athletes.roleClientsLower")
    : t("athletes.roleAthletesLower");
  const [filterValue, setFilterValue] = useState("");
  const filteredUsers = filterValue
    ? allUsers.filter((user) =>
        user.name.toLowerCase().startsWith(filterValue.toLowerCase())
      )
    : allUsers;

  return (
    <div id="step-1-group-chat">
      <div className="step1-group-chat">
        {!isTeamCoach && (
          <>
            <AutoManagedToggle
              isAutoManaged={isAutoManaged}
              hasAutoManagedChat={hasAutoManagedChat}
              handleToggleAutoManaged={handleToggleAutoManaged}
            />

            <AutoManagedInfoBox
              isAutoManaged={isAutoManaged}
              roleLower={roleLower}
              rolesLower={rolesLower}
            />
          </>
        )}

        {isAutoManaged && !isTeamCoach ? (
          <>
            <AutoManagedPreview
              activeAthletes={activeAthletes}
              webAppTheme={webAppTheme}
              roleSingularLower={roleLower}
              rolesLower={rolesLower}
            />
            {showCommunityTip && <CommunityTip />}
          </>
        ) : (
          <>
            <div className="search-user-group-chat">
              <input
                type="search"
                id="filter-group-chat"
                className="search-user-input-group-chat"
                placeholder={t("chat.newGroupModal.searchUserPlaceholder")}
                value={filterValue}
                onChange={({ target: { value } }) => setFilterValue(value)}
              />
            </div>
          </>
        )}
      </div>

      {!isAutoManaged && (
        <>
          {filteredUsers.length === 0 && filterValue.length > 0 ? (
            <div className="empty-filter-chat-state">
              <p className="s1">{t("chat.newGroupModal.noUsersFound")}</p>
            </div>
          ) : (
            <div className="gym-modal-main-table">
              <div className="add-participants-main-group-chat">
                <span className="add-participants-title-group-chat">
                  {t("chat.newGroupModal.addParticipants")}
                </span>
                <span className="add-participants-length-group-chat clipped">
                  (
                  {participantState.length && participantState[0].length === 0
                    ? 0
                    : participantState.length}
                  )
                </span>
              </div>
              <div className="list-group-chat-participants-main">
                {filteredUsers.map((item) => {
                  const { name = "", coachId = "", _id = "" } = item;

                  return (
                    <div
                      key={_id}
                      className="card-group-chat-participant"
                      onClick={() => handleSelectionChange(_id)}
                    >
                      <div>
                        <div
                          className={
                            item.avatar && item.avatar.s3Path
                              ? "avatar-group-chat"
                              : "avatar-group-chat avatar-group-chat-border"
                          }
                          style={{
                            background: `url(${getAvatarUrl(
                              item,
                              false,
                              webAppTheme
                            )})`,
                            backgroundPosition: "center center",
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                          }}
                        ></div>
                        <div className="identifier-group-chat">
                          <span className="name-group-chat">{name}</span>
                          <span className="role-group-chat">
                            {coachId
                              ? referName
                              : t("chat.newGroupModal.coach")}
                          </span>
                        </div>
                      </div>
                      <div
                        className={
                          participantState.includes(_id)
                            ? "circle-selector-container-selected"
                            : "circle-selector-container"
                        }
                      >
                        <div
                          className={
                            participantState.includes(_id)
                              ? "radio radio-group-selected-group-chat"
                              : "radio-video-list"
                          }
                        >
                          {participantState.includes(_id) && (
                            <img
                              src="/img/new-icons/vuesax-linear-tick-circle-gradient.svg"
                              alt={t("chat.newGroupModal.checkedAlt")}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Step2NewGroup = ({
  participantState,
  allUsers,
  createGroupButtonRef,
  referName,
  allGroupChats,
  webAppTheme,
  isAutoManaged,
}) => {
  const { t } = useTranslation("coaching-portal");
  const [groupInformationState, setGroupInformationState] = useState({
    image: "",
    subject: "",
  });

  function filterParticipants(acc, element) {
    if (participantState.includes(element._id)) {
      acc.push(element);
    }
    return acc;
  }

  const selectedUsers = allUsers.reduce(filterParticipants, []);

  const handleInputSubjectChange = ({ target }) => {
    const newState = Object.assign({}, groupInformationState, {
      [target.name]: target.value,
    });
    setGroupInformationState(newState);
  };

  return (
    <form
      encType="multipart/form-data"
      method="POST"
      data-noreset
      action={`/actions/chat/group-chat-save/`}
    >
      <div id="step-2-group-chat">
        <div className="step2-group-chat">
          <span className="add-participants-title-group-chat">
            {t("chat.newGroupModal.enterSubjectAndPicture")}
          </span>
          <div className="image-name-group-chat">
            <ImageChatUploadInput
              name="image"
              prefix={t("chat.newGroupModal.groupChatPrefix")}
              defaultValue={groupInformationState.image}
            />
            <div className="subject-group-chat">
              <input
                name="subject"
                className="search-user-input-group-chat"
                placeholder={t(
                  "chat.newGroupModal.enterGroupSubjectPlaceholder"
                )}
                value={groupInformationState.subject}
                onChange={handleInputSubjectChange}
              />
              <input
                name="participants"
                defaultValue={JSON.stringify(selectedUsers)}
                type="hidden"
              />
              <input
                name="allGroupChats"
                defaultValue={allGroupChats}
                type="hidden"
              />
              {isAutoManaged && (
                <input name="isAutoManaged" defaultValue="true" type="hidden" />
              )}
            </div>
          </div>
        </div>

        <div className="gym-modal-main-table">
          {!isAutoManaged && (
            <div className="add-participants-main-group-chat">
              <span className="add-participants-title-group-chat">
                {t("chat.newGroupModal.participantsCount", {
                  selected: selectedUsers.length,
                  total: allUsers.length,
                })}
              </span>
            </div>
          )}
          <div className="list-group-chat-participants-main">
            {selectedUsers.map((item) => {
              const { name = "", coachId = "", _id = "" } = item;

              return (
                <div key={_id} className="card-group-chat-participant">
                  <div>
                    <div
                      className={
                        item.avatar && item.avatar.s3Path
                          ? "avatar-group-chat"
                          : "avatar-group-chat avatar-group-chat-border"
                      }
                      style={{
                        background: `url(${getAvatarUrl(
                          item,
                          false,
                          webAppTheme
                        )})`,
                        backgroundPosition: "center center",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                      }}
                    ></div>
                    <div className="identifier-group-chat">
                      <span className="name-group-chat">{name}</span>
                      <span className="role-group-chat">
                        {coachId ? referName : t("chat.newGroupModal.coach")}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <button
        id="create-group-button"
        ref={createGroupButtonRef}
        style={{ display: "none" }}
      ></button>
    </form>
  );
};
